import { AddIcon } from "@chakra-ui/icons";
import { Box, BoxProps, Button, Flex } from "@chakra-ui/react";
import React, { memo } from "react";
import { useCurrentBreakpoint } from "../../../../hooks/useCurrentBreakpoint";

import { OrderFilterSetting } from "../../../../types/order/orderQuery";
import { FilterSection } from "./filters/FilterSection";
import { PaginationButtons } from "./PaginationButtons";
import { SelectedTransactionsActions } from "./SelectedTransactionsActions";

type TransactionsToolbarSectionProps = {
  pageContainerWidth: BoxProps["width"];

  filterSetting: OrderFilterSetting;
  setFilterSetting: React.Dispatch<React.SetStateAction<OrderFilterSetting>>;

  pageSize: number;

  totalItems: number;
  currentPageIndex: number;
  setCurrentPageIndex: React.Dispatch<React.SetStateAction<number>>;

  containerRef: React.Ref<HTMLDivElement>;

  filters: OrderFilterSetting;

  onAddTransaction: () => void;
};

export const TransactionsToolbarSection = memo(
  function TransactionsToolbarSection({
    pageContainerWidth,
    filterSetting,
    setFilterSetting,
    pageSize,
    totalItems,
    currentPageIndex,
    setCurrentPageIndex,
    containerRef,
    filters,
    onAddTransaction,
  }: TransactionsToolbarSectionProps): React.ReactElement {
    return (
      <Box
        ref={containerRef}
        w="full"
        minW="full"
        zIndex={6}
        position="sticky"
        top={0}
        px={{
          base: 0,
          "2xl": 8,
        }}
      >
        <Flex
          direction="column"
          position="sticky"
          left={0}
          maxW={pageContainerWidth}
          borderColor="gray.300"
          borderTopWidth="1px"
          borderLeftWidth="1px"
          borderRightWidth="1px"
        >
          <TransactionsToolbarControls
            filterSetting={filterSetting}
            setFilterSetting={setFilterSetting}
            pageSize={pageSize}
            totalItems={totalItems}
            currentPageIndex={currentPageIndex}
            setCurrentPageIndex={setCurrentPageIndex}
            onAddTransaction={onAddTransaction}
          />

          <SelectedTransactionsActions filters={filters} />
        </Flex>
      </Box>
    );
  },
);

type TransactionsToolbarControlsProps = {
  filterSetting: OrderFilterSetting;
  setFilterSetting: React.Dispatch<React.SetStateAction<OrderFilterSetting>>;

  pageSize: number;

  totalItems: number;
  currentPageIndex: number;
  setCurrentPageIndex: React.Dispatch<React.SetStateAction<number>>;

  onAddTransaction: () => void;
};

function TransactionsToolbarControls({
  filterSetting,
  setFilterSetting,
  pageSize,
  totalItems,
  currentPageIndex,
  setCurrentPageIndex,
  onAddTransaction,
}: TransactionsToolbarControlsProps): React.ReactElement {
  const currentBreakpoint = useCurrentBreakpoint();

  const isMediumScreen = currentBreakpoint.isAtLeast("md");

  const pagination =
    totalItems > 0 ?
      <PaginationButtons
        pageSize={pageSize}
        totalItems={totalItems}
        currentPageIndex={currentPageIndex}
        setCurrentPageIndex={setCurrentPageIndex}
      />
    : null;

  const layoutProps: TransactionsToolbarLayoutProps = {
    filterSetting,
    setFilterSetting,
    pagination,
    onAddTransaction,
  };

  if (isMediumScreen) {
    return <TransactionsToolbarLayoutMd {...layoutProps} />;
  }

  return <TransactionsToolbarLayoutBase {...layoutProps} />;
}

type TransactionsToolbarLayoutProps = {
  filterSetting: OrderFilterSetting;
  setFilterSetting: React.Dispatch<React.SetStateAction<OrderFilterSetting>>;
  pagination: React.ReactNode;
  onAddTransaction: () => void;
};

function TransactionsToolbarLayoutBase({
  filterSetting,
  setFilterSetting,
  pagination,
  onAddTransaction,
}: TransactionsToolbarLayoutProps): React.ReactElement {
  return (
    <Flex
      direction="row"
      align="flex-start"
      gap={2}
      px={2}
      py={2}
      wrap="wrap"
      backgroundColor="gray.50"
    >
      <FilterSection
        filterSetting={filterSetting}
        setFilterSetting={setFilterSetting}
      />

      <AddTransactionButton onClick={onAddTransaction} />

      {pagination && <Box>{pagination}</Box>}
    </Flex>
  );
}

function TransactionsToolbarLayoutMd({
  filterSetting,
  setFilterSetting,
  pagination,
  onAddTransaction,
}: TransactionsToolbarLayoutProps): React.ReactElement {
  return (
    <Flex
      px={2}
      py={2}
      gap={2}
      align="flex-start"
      justify="space-between"
      backgroundColor="gray.50"
    >
      <Flex gap={2} wrap="wrap">
        <FilterSection
          filterSetting={filterSetting}
          setFilterSetting={setFilterSetting}
        />
        <AddTransactionButton onClick={onAddTransaction} />
      </Flex>

      {pagination && <>{pagination}</>}
    </Flex>
  );
}

function AddTransactionButton({
  onClick,
}: {
  onClick: () => void;
}): React.ReactElement {
  return (
    <Button
      size="sm"
      flexShrink={0}
      variant="solid"
      leftIcon={<AddIcon />}
      colorScheme="primary"
      onClick={onClick}
      justifyContent="flex-start"
    >
      Add transaction
    </Button>
  );
}
